import {
  PLACEHOLDER,
  RequestRow,
  SortDirection,
  configurationLabel,
  displayValue,
  hourlyIndexLabelKey,
  sortRows
} from '../Requests/requestRows';
import { formatDate } from '../../../../../../utils';

export type TechnicianData = {
  name: string | null;
  email: string | null;
};

/**
 * Une demande de l'étape Affectation.
 *
 * Exactement la ligne de l'étape Demandes, plus le technicien affecté. Le champ n'est
 * pas ajouté à `RequestRow` parce que `GET pds/qualify/` ne le renvoie pas : le type
 * mentirait sur l'autre étape.
 */
export type AssignmentRow = RequestRow & { technician_data: TechnicianData | null };

/** Colonnes triables de la maquette. */
export type AssignmentSortColumn = 'servicePoint' | 'technician';

/** Un technicien sans nom ne compte pas comme une affectation, comme en v1. */
export function hasTechnician(row: AssignmentRow): boolean {
  return displayValue(row.technician_data?.name) !== PLACEHOLDER;
}

/** Nombre de demandes déjà affectées, donc prêtes à être envoyées. */
export function assignedCount(rows: AssignmentRow[]): number {
  return rows.filter(hasTechnician).length;
}

/**
 * Résumé de la qualification affiché sous le type d'intervention : configuration et
 * motif, séparés par un point médian, sans séparateur orphelin si l'un manque.
 */
export function qualificationCaption(row: AssignmentRow, t: (key: string) => string): string {
  const parts = [configurationLabel(row, t), displayValue(row.intervention_cause)].filter(
    (part) => part !== PLACEHOLDER
  );

  return parts.join(' · ');
}

/**
 * Configuration cible telle que l'affiche le détail déplié : le nom, suivi du critère
 * d'index horaires quand il a été posé. Les morceaux absents sont omis plutôt que
 * remplacés par un tiret orphelin.
 */
export function configurationSummary(row: AssignmentRow, t: (key: string) => string): string {
  const hourlyIndexKey = hourlyIndexLabelKey(row);
  const parts = [
    configurationLabel(row, t),
    hourlyIndexKey === null ? PLACEHOLDER : t(hourlyIndexKey)
  ].filter((part) => part !== PLACEHOLDER);

  return parts.length === 0 ? PLACEHOLDER : parts.join(' — ');
}

/**
 * Rendez-vous rédigé selon la règle métier plutôt qu'en deux bornes brutes : le technicien
 * lit directement « au plus tôt », « au plus tard » ou « entre ». Tiret sans rendez-vous.
 */
export function appointmentSummary(
  row: AssignmentRow,
  t: (key: string, options: Record<string, string>) => string,
  locale: string
): string {
  const start = row.start_scheduled_time && formatDate(row.start_scheduled_time, locale);
  const end = row.end_scheduled_time && formatDate(row.end_scheduled_time, locale);

  if (start && end) {
    return t('interventionCreation.assignment.appointment.between', { start, end });
  }
  if (start) {
    return t('interventionCreation.assignment.appointment.notBefore', { start });
  }
  if (end) {
    return t('interventionCreation.assignment.appointment.notAfter', { end });
  }
  return PLACEHOLDER;
}

/** Les demandes non affectées se regroupent : elles partagent la même clé de tri. */
export function sortAssignments(
  rows: AssignmentRow[],
  column: AssignmentSortColumn,
  direction: SortDirection
): AssignmentRow[] {
  return sortRows(
    rows,
    (row) =>
      column === 'servicePoint'
        ? (row.service_point_customer_id ?? '')
        : (row.technician_data?.name ?? ''),
    direction
  );
}
